import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  CampoDeMoeda,
  CampoDeSelecao,
  Campo,
  Modal,
  ValorFiscal,
  ValorPercentual,
} from '../../componentes/ui';
import { useCarteira, useClientesParaEscolher } from '../../hooks/useOperacao';
import { rotuloDoTipo, type Settings } from '../cliente/ConfiguracaoDeCredito';
import { requisicaoAutenticada } from '../../servicos/http';
import { lerValorDigitado, moedaParaCampo } from '../../servicos/formato';
import { ErroDaApi } from '../ErroDaApi';
import { CreditosNoSiscred } from './CreditosNoSiscred';
import { EnvioDaSimulacaoPontual } from './EnvioDaSimulacaoPontual';
import type { Negociacao, SimulacaoPontual } from './tipos';

/**
 * RN-701 — A simulação que vira negociação.
 *
 * ## Por que simula enquanto se digita
 *
 * Os três números que importam — o que o fornecedor recebe, o que o comprador
 * paga e o spread que sobra — saem de duas porcentagens. Mostrar o resultado
 * só depois de gravar transformaria a negociação numa tentativa e erro com
 * linhas no banco; aqui a conta aparece ao lado do formulário, e gravar é a
 * decisão de guardar o que já está na tela.
 *
 * A conta é do servidor, sempre: refazê-la no navegador criaria a segunda
 * implementação da mesma regra que o RN-078 proíbe.
 */
/**
 * Um ponto de partida para o formulário — o pedido de fechamento de um
 * assessor: o cliente dele como comprador, ele como assessor e os números que
 * ele simulou. O fornecedor fica para a operação escolher.
 */
export interface InicioDaNegociacao {
  readonly compradorId?: string;
  readonly parceiroId?: string;
  readonly valorFace?: number;
  readonly desagioCompraPercentual?: number;
  readonly desagioVendaPercentual?: number;
  readonly parcelas?: number;
}

const comoCampo = (percentual: number): string => String(percentual).replace('.', ',');

export function ModalDeNegociacao(props: {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  readonly aoCriar: (negociacao: Negociacao) => void;
  readonly inicio?: InicioDaNegociacao | null;
}): JSX.Element {
  const clientes = useClientesParaEscolher();
  const fornecedores = useCarteira('fornecedores');
  const assessores = useCarteira('assessores');

  const [compradorId, setCompradorId] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [fornecedorId, setFornecedorId] = useState('');
  const [parceiroId, setParceiroId] = useState('');
  const [tipoCredito, setTipoCredito] = useState('EXPORTACAO');
  const [valorFace, setValorFace] = useState('');
  const [desagioCompra, setDesagioCompra] = useState('20');
  const [desagioVenda, setDesagioVenda] = useState('12');
  const [parcelas, setParcelas] = useState('1');
  /** RN-706 — o deságio do cadastro vale até alguém dizer o contrário. */
  const [desagioLiberado, setDesagioLiberado] = useState(false);

  /**
   * RN-706 — O deságio de compra é o do cadastro do fornecedor.
   *
   * É lá que a operação mantém o combinado com cada fornecedor — a Durli
   * vende a 38%, e digitar isso de novo a cada negociação é onde o número
   * diverge do que foi acertado. A busca é pela configuração da empresa, a
   * mesma tela que cadastra o deságio, e não por uma cópia aqui.
   */
  const configuracaoDoFornecedor = useQuery({
    queryKey: ['icms-settings-business', fornecedorId],
    queryFn: () => requisicaoAutenticada<Settings>(`/icms/settings/business/${fornecedorId}`),
    enabled: props.aberto && fornecedorId !== '',
  });

  const creditosDoFornecedor = configuracaoDoFornecedor.data?.creditos ?? [];
  // O tipo escolhido tem precedência; com um cadastro só, ele vale para
  // qualquer tipo — é o caso da base de hoje, onde quase todo fornecedor tem
  // uma linha "Padrão" herdada do sistema antigo.
  const doCadastro =
    creditosDoFornecedor.find((c) => c.tipoCredito === tipoCredito) ??
    (creditosDoFornecedor.length === 1 ? creditosDoFornecedor[0] : undefined);
  const desagioDoCadastro = doCadastro?.desagioPercentual;
  const travado = desagioDoCadastro !== undefined && !desagioLiberado;

  // Trocar de fornecedor (ou de tipo) traz o deságio dele e volta a travar: o
  // número digitado para o fornecedor anterior não vale para o novo.
  useEffect(() => {
    if (desagioDoCadastro === undefined) return;
    setDesagioCompra(String(desagioDoCadastro).replace('.', ','));
    setDesagioLiberado(false);
  }, [desagioDoCadastro, fornecedorId]);

  const face = lerValorDigitado(valorFace) ?? 0;
  const compra = Number(desagioCompra.replace(',', '.'));
  const venda = Number(desagioVenda.replace(',', '.'));
  const vezes = Math.max(1, Number(parcelas) || 1);

  const podeSimular =
    face > 0 && Number.isFinite(compra) && Number.isFinite(venda) && venda <= compra;

  const simulacao = useQuery({
    queryKey: ['pontual-simulacao', face, compra, venda, vezes],
    queryFn: () =>
      requisicaoAutenticada<SimulacaoPontual>('/pontual/simulacoes', {
        metodo: 'POST',
        corpo: {
          valorFace: face,
          desagioCompraPercentual: compra,
          desagioVendaPercentual: venda,
          parcelas: vezes,
        },
      }),
    enabled: props.aberto && podeSimular,
  });

  const criar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<Negociacao>('/pontual/negociacoes', {
        metodo: 'POST',
        corpo: {
          compradorId,
          fornecedorId,
          ...(parceiroId === '' ? {} : { parceiroId }),
          tipoCredito,
          valorFace: face,
          desagioCompraPercentual: compra,
          desagioVendaPercentual: venda,
          parcelas: vezes,
        },
      }),
    onSuccess: props.aoCriar,
  });

  // Fechar e reabrir não pode trazer a negociação anterior meio preenchida.
  useEffect(() => {
    if (!props.aberto) {
      criar.reset();
      setValorFace('');
    }
    // `criar` é estável o bastante para o efeito; o que importa é `aberto`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.aberto]);

  // Aberto a partir de um pedido: o formulário já vem com o que o assessor viu.
  const inicio = props.inicio;
  useEffect(() => {
    if (!props.aberto || inicio === undefined || inicio === null) return;
    if (inicio.compradorId !== undefined) setCompradorId(inicio.compradorId);
    if (inicio.parceiroId !== undefined) setParceiroId(inicio.parceiroId);
    if (inicio.valorFace !== undefined) setValorFace(moedaParaCampo(inicio.valorFace));
    if (inicio.desagioCompraPercentual !== undefined) {
      setDesagioCompra(comoCampo(inicio.desagioCompraPercentual));
      // O número do assessor vale até a operação escolher o fornecedor — aí o
      // deságio do cadastro dele assume (RN-706), como em qualquer negociação.
      setDesagioLiberado(true);
    }
    if (inicio.desagioVendaPercentual !== undefined) {
      setDesagioVenda(comoCampo(inicio.desagioVendaPercentual));
    }
    if (inicio.parcelas !== undefined) setParcelas(String(inicio.parcelas));
  }, [props.aberto, inicio]);

  const resultado = simulacao.data;
  const completo = compradorId !== '' && fornecedorId !== '' && podeSimular;

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Nova negociação de crédito"
      descricao="Compra do fornecedor, venda ao comprador. O spread entre as duas pontas é o honorário do assessor."
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="secundario"
            disabled={resultado === undefined || !podeSimular}
            onClick={() => setEnviandoEmail(true)}
          >
            Enviar por e-mail
          </Botao>
          <Botao disabled={!completo} carregando={criar.isPending} onClick={() => criar.mutate()}>
            Gravar negociação
          </Botao>
        </>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeSelecao
              rotulo="Fornecedor (vende)"
              value={fornecedorId}
              onChange={(e) => setFornecedorId(e.target.value)}
              opcoes={[
                { valor: '', rotulo: 'Selecione…' },
                ...(fornecedores.data ?? []).map((f) => ({
                  valor: f.businessId,
                  rotulo: f.razaoSocial,
                })),
              ]}
            />
            <CampoDeSelecao
              rotulo="Comprador"
              value={compradorId}
              onChange={(e) => setCompradorId(e.target.value)}
              opcoes={[
                { valor: '', rotulo: 'Selecione…' },
                ...(clientes.data ?? []).map((c) => ({
                  valor: c.businessId,
                  rotulo: c.razaoSocial,
                })),
              ]}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeSelecao
              rotulo="Assessor"
              ajuda="Quem intermedeia. Sem assessor, a operadora atende direto."
              value={parceiroId}
              onChange={(e) => setParceiroId(e.target.value)}
              opcoes={[
                { valor: '', rotulo: 'Sem assessor (operadora)' },
                ...(assessores.data ?? []).map((a) => ({
                  valor: a.businessId,
                  rotulo: a.razaoSocial,
                })),
              ]}
            />
            <CampoDeSelecao
              rotulo="Tipo de crédito"
              value={tipoCredito}
              onChange={(e) => setTipoCredito(e.target.value)}
              opcoes={[
                { valor: 'EXPORTACAO', rotulo: 'Crédito de exportação' },
                { valor: 'DIFERIDO', rotulo: 'Crédito diferido' },
                { valor: 'OUTRO', rotulo: 'Outro crédito acumulado' },
              ]}
            />
          </div>

          {/*
            Duas colunas, e não três: em três, "Deságio de venda (%)" quebrava
            em duas linhas enquanto "Parcelas" ficava em uma, e os campos
            desalinhavam no meio do formulário. Aos pares, cada linha tem
            rótulos do mesmo tamanho.
          */}
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoDeMoeda
              rotulo="Valor de face do crédito"
              ajuda="Quanto o crédito vale na apuração de quem comprar."
              value={valorFace}
              onChange={setValorFace}
            />
            <Campo
              rotulo="Parcelas"
              numerico
              ajuda="1 = à vista; acima disso, mensais."
              value={parcelas}
              onChange={(e) => setParcelas(e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              {/*
                Os nomes pelo lado de quem dá o deságio, como na tabela
                (14/10/2026): o fornecedor vende — "Deságio de venda"; o
                comprador compra — "Deságio de compra". No código os campos
                seguem a operação (`desagioCompra` é o do fornecedor).
              */}
              <Campo
                rotulo="Deságio de venda"
                numerico
                sufixo="%"
                disabled={travado}
                ajuda={
                  travado
                    ? 'Vem do cadastro do fornecedor.'
                    : desagioDoCadastro === undefined
                      ? 'O desconto que o fornecedor dá.'
                      : 'Vale só nesta negociação — o cadastro do fornecedor não muda.'
                }
                value={desagioCompra}
                onChange={(e) => setDesagioCompra(e.target.value)}
              />
              {desagioDoCadastro !== undefined && (
                <button
                  type="button"
                  className="self-start text-xs text-acento-ciano underline-offset-4 hover:underline"
                  onClick={() => {
                    if (travado) {
                      setDesagioLiberado(true);
                      return;
                    }
                    setDesagioCompra(String(desagioDoCadastro).replace('.', ','));
                    setDesagioLiberado(false);
                  }}
                >
                  {travado
                    ? 'Editar nesta negociação'
                    : `Voltar ao cadastro (${String(desagioDoCadastro).replace('.', ',')}%)`}
                </button>
              )}
              {/*
                Vários tipos cadastrados e nenhum igual ao escolhido: a tela não
                escolhe por conta própria — mostra o que existe e deixa a pessoa
                decidir, que é melhor que aplicar o deságio do tipo errado.
              */}
              {desagioDoCadastro === undefined && creditosDoFornecedor.length > 0 && (
                <span className="text-xs text-conteudo-sutil">
                  No cadastro deste fornecedor:{' '}
                  {creditosDoFornecedor
                    .map((c) => `${rotuloDoTipo(c.tipoCredito)} ${c.desagioPercentual}%`)
                    .join(' · ')}
                  .
                </span>
              )}
            </div>
            <Campo
              rotulo="Deságio de compra"
              numerico
              sufixo="%"
              ajuda="O desconto que o comprador recebe."
              value={desagioVenda}
              onChange={(e) => setDesagioVenda(e.target.value)}
            />
          </div>

          {venda > compra && (
            <Aviso tom="atencao" titulo="O deságio do comprador passou o do fornecedor">
              O comprador pagaria menos do que o fornecedor recebe — o spread ficaria negativo, e o
              assessor pagaria para intermediar.
            </Aviso>
          )}

          {criar.isError && <ErroDaApi titulo="Não foi possível gravar" erro={criar.error} />}
        </div>

        {/*
          O resultado ao lado: é ele que a pessoa está negociando. Embaixo dele,
          os créditos do fornecedor no SISCRED (29/10/2026) — a coluna estreita
          comporta a lista sem espremer o formulário.
        */}
        <div className="flex h-fit flex-col gap-5">
          <aside className="flex h-fit flex-col gap-3 rounded-painel border border-borda bg-superficie-elevada p-4">
            <h3 className="font-display text-sm font-semibold tracking-tight">A negociação</h3>

            {resultado === undefined ? (
              <p className="text-sm text-conteudo-sutil">
                Informe o valor de face e os dois deságios para ver os números.
              </p>
            ) : (
              <dl className="flex flex-col gap-3">
                <Linha rotulo="Fornecedor recebe">
                  <ValorFiscal valor={resultado.valorCompra} />
                </Linha>
                <Linha rotulo="Comprador paga">
                  <ValorFiscal valor={resultado.valorVenda} />
                </Linha>
                <Linha rotulo="Spread (honorários)">
                  <span className="flex items-baseline gap-2">
                    <ValorFiscal valor={resultado.spread} variante="economia" />
                    <ValorPercentual percentual={resultado.spreadPercentual} tamanho="pequeno" />
                  </span>
                </Linha>
                <Linha rotulo="Economia do comprador">
                  <ValorFiscal valor={resultado.economiaDoComprador} variante="economia" />
                </Linha>

                {vezes > 1 && (
                  <div className="border-t border-borda pt-3">
                    <p className="text-xs uppercase tracking-wide text-conteudo-sutil">
                      {vezes} parcelas
                    </p>
                    <p className="mt-1 text-sm text-conteudo-secundario">
                      Comprador{' '}
                      <ValorFiscal valor={resultado.parcelas[0]?.valor} tamanho="pequeno" /> ·
                      honorários{' '}
                      <ValorFiscal valor={resultado.honorarios[0]?.valor} tamanho="pequeno" />
                    </p>
                  </div>
                )}
              </dl>
            )}

            {simulacao.isError && (
              <ErroDaApi titulo="Não foi possível simular" erro={simulacao.error} />
            )}
          </aside>
          {fornecedorId !== '' && (
            <CreditosNoSiscred
              fornecedorId={fornecedorId}
              aoEscolher={(escolha) => {
                setValorFace(moedaParaCampo(escolha.valor));
                setTipoCredito(escolha.tipoCredito);
              }}
            />
          )}
        </div>
      </div>
      {enviandoEmail && (
        <EnvioDaSimulacaoPontual
          parametros={{
            valorFace: face,
            desagioCompraPercentual: compra,
            desagioVendaPercentual: venda,
            parcelas: vezes,
          }}
          {...(compradorId === '' ? {} : { compradorId })}
          {...(fornecedorId === '' ? {} : { fornecedorId })}
          aoFechar={() => setEnviandoEmail(false)}
        />
      )}
    </Modal>
  );
}

function Linha(props: {
  readonly rotulo: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">{props.children}</dd>
    </div>
  );
}
